import { inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { combineLatest, EMPTY, from, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { PaginatedStateEffects } from '@shared/ui';
import { useLocalization } from '@shared/localization';
import type { AssignedPipelineOutput, IListAssignedPipelinesQuery } from '@protocols/use-cases';

import { AssignedPipelinesState } from '../state/assigned-pipelines.state';
import type { AssignedPipelinesStateModel, MethodPipelineContext } from '../state/assigned-pipelines.state';
import { PipelineData } from '../state/pipeline.state';

export const LIST_ASSIGNED_PIPELINES_QUERY = new InjectionToken<IListAssignedPipelinesQuery>(
  'LIST_ASSIGNED_PIPELINES_QUERY'
);

/**
 * Effects for the right list of the pipeline manager. Fetches the assigned set
 * once on init, then windows the combined (persisted + staged) set client-side.
 * Page size is 10; there is no search. Staging mutations relist + recount so the
 * paginated window reflects them immediately (workspace-members pattern).
 */
@Injectable()
export class AssignedPipelinesEffects extends PaginatedStateEffects<PipelineData, AssignedPipelinesStateModel> {
  private readonly listAssignedPipelinesQuery = inject<IListAssignedPipelinesQuery>(LIST_ASSIGNED_PIPELINES_QUERY);

  public currentLanguage$: Observable<string>;

  constructor(protected readonly assignedState: AssignedPipelinesState) {
    super(assignedState);
    this.currentLanguage$ = useLocalization().currentLanguage$;
  }

  // ============================================================================
  // Re-exposed selectors / updater aliases
  // ============================================================================

  public readonly assignedIds$ = this.assignedState.assignedIds$;
  public readonly persistedLoaded$ = this.assignedState.persistedLoaded$;

  public readonly setPersistedAssigned = this.assignedState.setPersistedAssigned;

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Point the state at the opened method type and load its assigned page.
   *
   * The staging is keyed per method type, so cross-type isolation is structural:
   * each method type keeps its own staged slice across dialog opens, and
   * reopening one restores exactly what was left there. When this session has
   * ALREADY loaded the method type's persisted set, the retained slice is the
   * source of truth — re-fetching would clobber session staging — so we just
   * rebuild the page window from it. Otherwise we fetch the persisted set (or
   * seed empty for a brand-new, not-yet-persisted method type). Errors fall back
   * to an empty set so the list still renders.
   */
  public readonly initialize = this.effect((context$: Observable<MethodPipelineContext>) =>
    context$.pipe(
      tap((ctx) => this.assignedState.setContext(ctx)),
      concatLatestFrom(() => this.assignedState.persistedLoaded$),
      switchMap(([ctx, alreadyLoaded]) => {
        if (alreadyLoaded) {
          this.initializeItems();
          return EMPTY;
        }
        const fetch$ =
          ctx.persisted && ctx.methodTypeId
            ? from(this.listAssignedPipelinesQuery.execute(ctx.methodTypeId)).pipe(
                map((pipelines) => pipelines.map(this.toPipelineData))
              )
            : of<PipelineData[]>([]);
        return fetch$.pipe(
          tapResponse(
            (assigned) => {
              this.assignedState.setPersistedAssigned(assigned);
              this.initializeItems();
            },
            (error: Error) => {
              this.assignedState.setPersistedAssigned([]);
              this.assignedState.setError(error.message);
              this.initializeItems();
            }
          )
        );
      })
    )
  );

  /** Stage a pipeline as assigned and refresh the current page + count. */
  public readonly assign = this.effect((pipeline$: Observable<PipelineData>) =>
    pipeline$.pipe(
      tap((pipeline: PipelineData) => {
        this.assignedState.assign(pipeline);
        this.relistAndRecount();
      })
    )
  );

  /** Un-stage a pipeline assignment and refresh the current page + count. */
  public readonly unassign = this.effect((id$: Observable<string>) =>
    id$.pipe(
      tap((id: string) => {
        this.assignedState.unassign(id);
        this.relistAndRecount();
      })
    )
  );

  /** Reflect a dialog edit of an assigned pipeline in the current page. */
  public readonly applyEdit = this.effect((pipeline$: Observable<PipelineData>) =>
    pipeline$.pipe(
      tap((pipeline: PipelineData) => {
        this.assignedState.updateAssignedPipeline(pipeline);
        this.relistPage();
      })
    )
  );

  // Legacy-style pagination alias to mirror the other paginated effects.
  public readonly loadMoreAssigned = this.loadMoreItems;

  private relistAndRecount(): void {
    this.relistPage();
    this.countItems();
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(): Promise<number> {
    const combined = await this.assignedState.combinedAssigned$.pipe(take(1)).toPromise();
    return combined?.length ?? 0;
  }

  protected listFlow(
    limit: number,
    lastItem: PipelineData | undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _searchTerm: string | undefined
  ): Observable<PipelineData[]> {
    return combineLatest([this.assignedState.combinedAssigned$, this.currentLanguage$]).pipe(
      take(1),
      map(([combined, lang]) => {
        const sorted = [...combined].sort((a, b) => this.compare(a, b, lang));
        const cursor = lastItem ? this.cursorKey(lastItem, lang) : undefined;
        return sorted
          .filter((p) => cursor === undefined || this.cursorKey(p, lang).localeCompare(cursor) > 0)
          .slice(0, limit);
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected processDeferredAttributes(_itemId: string): Observable<Partial<PipelineData>> {
    return of();
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  private cursorKey(pipeline: PipelineData, lang: string): string {
    const name = pipeline.name[lang] ?? '';
    return `${name}_${pipeline.id}`;
  }

  private compare(a: PipelineData, b: PipelineData, lang: string): number {
    return this.cursorKey(a, lang).localeCompare(this.cursorKey(b, lang));
  }

  private toPipelineData(output: AssignedPipelineOutput): PipelineData {
    return { id: output.id, name: output.name, env: output.env, createdAt: output.createdAt };
  }
}
